import { TrackingEventKey } from "@next-platform/packages-ts-types";

import { MetaStandardEvent } from "@/features/tracking/providers/meta/metaPixel";

/**
 * Frontend mirror of the backend Meta event map
 * (`spicychat-backend/src/marketing/capi/config/metaCapiConfig.ts`).
 *
 * Duplication is intentional and low-risk:
 * - Vendor event names are externally versioned by Meta and change
 *   far slower than our internal `TrackingEventKey` catalog.
 * - The two repos don't share a runtime package; extracting this map
 *   into `common-ts-model` would force the shared package to depend
 *   on vendor SDK constants it has no other reason to know about.
 *
 * Every entry is a Meta **standard event** — no Custom Event
 * registration is required in Events Manager. Subscription renewals
 * ride the standard `Purchase` event on the backend (see backend
 * `PAYMENT_TYPE_TO_EVENT_KEY`); the frontend only fires `Subscribe`
 * or `Purchase` from CheckoutSuccess.
 *
 * Browser-only intent keys (`PaywallViewed`, `CheckoutStarted`) map
 * to Meta standard events here but are **never** produced on the
 * backend — intentional: they fire via Meta Pixel only, and the
 * backend `metaEventMap` omits them so CAPI `supports()` returns
 * false if one ever leaks through.
 *
 * Keys not listed here are intentionally unmapped for Meta (e.g.
 * future experimental events) and `fireConversion` will no-op for
 * the Meta provider on those keys.
 */
const metaEventMap: Partial<Record<TrackingEventKey, MetaStandardEvent>> = {
  [TrackingEventKey.Signup]: MetaStandardEvent.COMPLETE_REGISTRATION,
  [TrackingEventKey.PaywallViewed]: MetaStandardEvent.VIEW_CONTENT,
  [TrackingEventKey.CheckoutStarted]: MetaStandardEvent.INITIATE_CHECKOUT,
  [TrackingEventKey.TrialStart]: MetaStandardEvent.START_TRIAL,
  [TrackingEventKey.Subscribe]: MetaStandardEvent.SUBSCRIBE,
  [TrackingEventKey.Purchase]: MetaStandardEvent.PURCHASE,
};

export default function resolveMetaEventName(eventKey: TrackingEventKey): MetaStandardEvent | undefined {
  return metaEventMap[eventKey];
}
